import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo, memo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import Skeleton from 'react-loading-skeleton';

import { IconRcStreamDrawWinner } from '@/assets/stream';
import { EmptyRecord, FollowButton, UnionAvatar, UserCardPopup, Button } from '@/components';
import {
  useCurrentUserId,
  isOfficalAddress,
  openNewWindow,
  getOfficialPath,
  wrapModalPromise,
  WrapModalProps,
} from '@/utils';
import {
  useFeedDrawJoiners,
  useArticleQuestJoiners,
  FeedDrawJoiner,
  useGetAllFeedDrawWinner,
} from '@/views/Stream';
import { copyFn } from '@/components/shared/CopyImage';

import style from './index.module.scss';

type Props = {
  id: number;
  isWinner?: boolean;
};

const JoinerModal = ({ id, isWinner }: Props & WrapModalProps) => {
  const { t } = useTranslation();
  const { mutateAsync: getAllWinner, isLoading: isFetchingAllWinner } = useGetAllFeedDrawWinner(id);

  const { isLoading, result, hasNextPage, isFetchingNextPage, fetchNextPage } = (
    isWinner ? useFeedDrawJoiners : useArticleQuestJoiners
  )(id, isWinner);

  const list = 'totalCount' in result ? result.list : result.users;

  const fetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleCopy = useCallback(async () => {
    const res = await getAllWinner();
    const content = res?.users?.map(i => i.id)?.join(',');
    if (content) {
      copyFn(content);
    }
  }, [getAllWinner]);

  return (
    <div className={style.container}>
      <div className={style.header}>
        <div className={style.title}>
          {isWinner ? t('DrawJoinersModal.winners') : t('DrawJoinersModal.participants')}
        </div>
        {isWinner && list?.length ? (
          <Button
            className={style.copyWinnerBtn}
            onClick={handleCopy}
            loading={isFetchingAllWinner}>
            {t('DrawJoinersModal.copyBtn')}
          </Button>
        ) : null}
      </div>
      <div className={style.tableHeader}>
        <div className={style.leftContent}>{t('DrawJoinersModal.1kpnv')}</div>
      </div>
      {isLoading ? (
        <SkeletonList />
      ) : list?.length ? (
        <Virtuoso
          style={{ height: '500px' }}
          data={list}
          overscan={20}
          endReached={fetchMore}
          itemContent={(index, item) => (
            <ListItem data={item as FeedDrawJoiner} key={`${item.id}_${index}`} />
          )}
        />
      ) : (
        <EmptyRecord />
      )}
    </div>
  );
};

export const openJoinerModal = wrapModalPromise<Props>(JoinerModal, {
  size: 'normal',
});

const ListItem = ({ data }: { data: FeedDrawJoiner }) => {
  const address = useCurrentUserId();
  const isOfficial = useMemo(() => isOfficalAddress(data.id), [data.id]);

  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      openNewWindow(
        isOfficial
          ? `${getOfficialPath(data as OfficialAccount)}/stream`
          : `/profile/${data.id}/stream`,
        '_blank',
      );
    },
    [data, isOfficial],
  );

  return (
    <div className={style.item}>
      <div className={style.avatarWrap} onClick={handleUserClick}>
        <UnionAvatar className={style.avatar} size={48} data={data} userCardPopup />
        {data.is_winner ? <IconRcStreamDrawWinner className={style.winnerIcon} /> : null}
        <UserCardPopup
          address={isOfficalAddress(data.id) ? '' : data.id || ''}
          className={style.userName}>
          {data._author}
        </UserCardPopup>
      </div>
      {data.id === address || isOfficial ? null : (
        <FollowButton address={data.id} userInfo={data as any} className={style.followBtn} />
      )}
    </div>
  );
};

const SkeletonList = memo(({ count = 5 }) => (
  <>
    {Array(count).fill(
      <div className={style.item}>
        <div className={style.avatarWrap}>
          <Skeleton circle height={48} width={48} className={style.avatar} />
          <Skeleton width={80} height={18} className={style.userName} />
        </div>
        <Skeleton className={style.followBtn} />
      </div>,
    )}
  </>
));
